import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Tabs } from "@shared/ui/tabs";
import { PremiseTab } from "./premiseTab";
import { MetersTab } from "./metersTab";
import { useParams } from "react-router-dom";
import { useAddPremisesModal } from "../../hooks";


interface AddPremiseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  routeDetails:any
   onSuccess?: () => void; 
}

export const AddPremiseModal: React.FC<AddPremiseModalProps> = ({
  open,
  onOpenChange,
  routeDetails,
  onSuccess
}) => {
  const [selectedPremises, setSelectedPremises] = useState<string[]>([]);

  // ✅ get routeId from URL or props
  const { route_id } = useParams();

  // ✅ mutation hook
  const addPremisesMutation = useAddPremisesModal(route_id);

  // ✅ handle submit
  const handleAddPremises = async () => {
    if (!selectedPremises.length) return;

    const payload = {
      premises: selectedPremises,
    };

    try {
      await addPremisesMutation.mutateAsync(payload);
      onOpenChange(false); // close dialog after success
      onSuccess?.();
    } catch (error) {
      console.error("Failed to add premises:", error);
    }
  };

  const tabComponents = {
    premises: {
      label: "Premises",
      shortLabel: "premises",
      component: (
        <PremiseTab
          selectedPremises={selectedPremises}
          setSelectedPremises={setSelectedPremises}
          initialPremises={routeDetails?.premise ?? []}
        />
      ),
    },
    meters: {
      label: "Meters",
      shortLabel: "meters",
      component: <MetersTab selectedPremises={selectedPremises} />,
    },
  };

  const urlMapping = {
    premises: "premises",
    meters: "meters",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader className="flex items-start justify-between">
          <div>
            <DialogTitle className="text-xl">Add Premises to Route</DialogTitle>
            <DialogDescription>
              The premises and meters shown below are derived from your current
              dataset. Use search to locate items. This flow mirrors the add
              form experience and helps you quickly review and select premises
              to include.
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="py-6">
          <Tabs
            defaultValue="premises"
            urlMapping={urlMapping}
            tabComponents={tabComponents}
            tabsListFullWidth={true}
            tabsListClassName="grid grid-cols-2"
            disableUrlSync={true}
          />
        </div>


        <DialogFooter className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            onClick={handleAddPremises} // ✅ trigger mutation
            disabled={addPremisesMutation.isPending}
          >
            {addPremisesMutation.isPending ? "Adding..." : "Done"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
